/**
 * Step-up re-verification, client half (guide §7.2).
 *
 * The service gates six review verbs — approve, batch-approve, reject,
 * reverse, and the two reversal verdicts — behind `X-StepUp-Assertion`
 * (`app/platform/stepup.py`), refusing with the machine codes
 * `STEPUP_REQUIRED` / `STEPUP_INVALID`. This module is the ONE place the
 * assertion comes from, so when core-mfa grows a real challenge flow the
 * change lands here and nowhere else.
 *
 * Today the mint is `POST {AUTH_API}/stepup`: core-stub answers it (solo
 * mode — it signs with the same key its JWKS serves, `acr: stepup`,
 * audience-bound, 5-minute expiry). The REAL core-mfa does not mint step-up
 * assertions yet — that is a platform gap recorded in
 * PLATFORM_INTEGRATION.md — so in attached mode the mint 404s and the verbs
 * surface the honest copy below rather than a raw error.
 *
 * The assertion is cached until shortly before expiry: one factor
 * re-verification should cover a review SESSION (a checker approving forty
 * resolutions), not cost one challenge per click.
 */
import { ApiError, postWithStepUp } from '../api/client'
import { AUTH_API } from './config'
import { session } from './session'

let cached: { assertion: string; exp: number } | null = null

/** Drop the cached assertion — sign-out, or a server-side STEPUP_INVALID. */
export function clearStepUp(): void {
  cached = null
}

async function mint(): Promise<string> {
  const res = await session.authed(`${AUTH_API}/stepup`, { method: 'POST' })
  if (!res.ok) {
    // 404/405: the platform has no step-up mint (real core-mfa, today).
    // Anything else: a real refusal worth reading.
    throw new ApiError(
      'STEPUP_UNAVAILABLE',
      res.status === 404 || res.status === 405
        ? 'Re-verification is not available on this platform yet — the review verbs need core-mfa step-up, which has not been enabled.'
        : `Could not re-verify (HTTP ${res.status}).`,
      res.status,
    )
  }
  const body = (await res.json()) as { assertion: string; expires_in?: number }
  const ttl = Math.max(30, (body.expires_in ?? 300) - 30)
  cached = { assertion: body.assertion, exp: Date.now() + ttl * 1000 }
  return body.assertion
}

async function assertion(fresh = false): Promise<string> {
  if (!fresh && cached && cached.exp > Date.now()) return cached.assertion
  return mint()
}

/** A step-up-gated POST: mint (or reuse) an assertion, send it, and on a
 *  server-side `STEPUP_INVALID`/`STEPUP_REQUIRED` re-mint once and retry —
 *  the assertion may have expired between cache check and arrival. */
export async function stepUpPost<T>(path: string, body: unknown): Promise<T> {
  try {
    return await postWithStepUp<T>(path, body, await assertion())
  } catch (e) {
    if (e instanceof ApiError && (e.code === 'STEPUP_INVALID' || e.code === 'STEPUP_REQUIRED')) {
      clearStepUp()
      return postWithStepUp<T>(path, body, await assertion(true))
    }
    throw e
  }
}
